import { useMemo } from 'react';
import { Box, Skeleton, Typography } from '@mui/material';
import {
  AreaChart,
  Area,
  XAxis,
  YAxis,
  CartesianGrid,
  Tooltip,
  ResponsiveContainer,
  Legend,
} from 'recharts';
import { format } from 'date-fns';
import { REQUEST_TYPE_COLORS } from '../theme';
import type { TimeSeriesPoint } from '../types';

interface DailyTrendsChartProps {
  data: TimeSeriesPoint[];
  isLoading: boolean;
}

const REQUEST_TYPES_CONFIG = [
  { key: 'workflow', label: 'Workflows', color: REQUEST_TYPE_COLORS.workflow },
  { key: 'query', label: 'Queries', color: REQUEST_TYPE_COLORS.query },
  { key: 'agent', label: 'Agent', color: REQUEST_TYPE_COLORS.agent },
  { key: 'compare', label: 'Compare', color: REQUEST_TYPE_COLORS.compare },
  { key: 'checklist', label: 'Checklist', color: REQUEST_TYPE_COLORS.checklist },
  { key: 'other', label: 'Other', color: REQUEST_TYPE_COLORS.other },
] as const;

interface ChartDataPoint {
  timestamp: string;
  dateLabel: string;
  workflow: number;
  query: number;
  agent: number;
  compare: number;
  checklist: number;
  other: number;
  total: number;
}

interface TooltipPayloadEntry {
  dataKey: string;
  name: string;
  value: number;
  color: string;
}

interface CustomTooltipProps {
  active?: boolean;
  payload?: TooltipPayloadEntry[];
  label?: string;
}

function CustomTooltip({ active, payload, label }: CustomTooltipProps) {
  if (!active || !payload) return null;

  const total = payload.reduce((sum, entry) => sum + (entry.value || 0), 0);
  const nonZeroPayload = payload.filter((entry) => entry.value > 0);

  if (nonZeroPayload.length === 0) return null;

  return (
    <Box
      sx={{
        bgcolor: '#1e293b',
        border: '1px solid rgba(255,255,255,0.1)',
        borderRadius: 1,
        p: 1.5,
        minWidth: 140,
      }}
    >
      <Typography variant="caption" sx={{ color: '#94a3b8', display: 'block', mb: 0.5 }}>
        {label}
      </Typography>
      <Typography variant="body2" sx={{ fontWeight: 600, color: '#f8fafc', mb: 0.75 }}>
        Total: {total.toLocaleString()}
      </Typography>
      {nonZeroPayload
        .sort((a, b) => b.value - a.value)
        .map((entry) => (
          <Box key={entry.dataKey} sx={{ display: 'flex', alignItems: 'center', gap: 0.75, mb: 0.25 }}>
            <Box sx={{ width: 8, height: 8, borderRadius: '2px', bgcolor: entry.color, flexShrink: 0 }} />
            <Typography variant="caption" sx={{ color: '#e2e8f0' }}>
              {entry.name}: {entry.value.toLocaleString()}
            </Typography>
          </Box>
        ))}
    </Box>
  );
}

export default function DailyTrendsChart({ data, isLoading }: DailyTrendsChartProps) {
  const chartData = useMemo<ChartDataPoint[]>(() => {
    return data.map((point) => ({
      timestamp: point.timestamp,
      dateLabel: format(new Date(point.timestamp), 'MMM d'),
      workflow: point.byType?.workflow || 0,
      query: point.byType?.query || 0,
      agent: point.byType?.agent || 0,
      compare: point.byType?.compare || 0,
      checklist: point.byType?.checklist || 0,
      other: point.byType?.other || 0,
      total: point.count,
    }));
  }, [data]);

  const activeTypes = useMemo(() => {
    const typeTotals: Record<string, number> = {};
    chartData.forEach((point) => {
      REQUEST_TYPES_CONFIG.forEach(({ key }) => {
        typeTotals[key] = (typeTotals[key] || 0) + point[key];
      });
    });
    return REQUEST_TYPES_CONFIG.filter(({ key }) => typeTotals[key] > 0);
  }, [chartData]);

  if (isLoading) {
    return (
      <Box sx={{ height: 280 }}>
        <Skeleton variant="rounded" height={280} animation="wave" sx={{ bgcolor: 'rgba(255,255,255,0.05)' }} />
      </Box>
    );
  }

  if (data.length === 0) {
    return (
      <Box sx={{ height: 280, display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
        <Typography variant="body2" color="text.secondary">
          No trend data available for the selected period
        </Typography>
      </Box>
    );
  }

  return (
    <ResponsiveContainer width="100%" height={280}>
      <AreaChart data={chartData}>
        <CartesianGrid strokeDasharray="3 3" stroke="rgba(148, 163, 184, 0.1)" />
        <XAxis
          dataKey="dateLabel"
          stroke="#94a3b8"
          tick={{ fontSize: 11 }}
          tickMargin={8}
        />
        <YAxis
          stroke="#94a3b8"
          tick={{ fontSize: 11 }}
          tickFormatter={(value: number) => value.toLocaleString()}
        />
        <Tooltip content={<CustomTooltip />} />
        <Legend
          wrapperStyle={{ paddingTop: 12 }}
          iconType="square"
          iconSize={10}
        />
        {activeTypes.map(({ key, label, color }) => (
          <Area
            key={key}
            type="monotone"
            dataKey={key}
            name={label}
            stroke={color}
            fill={color + '80'}
            strokeWidth={2}
            stackId="requests"
          />
        ))}
      </AreaChart>
    </ResponsiveContainer>
  );
}
